/**
 * The catalog's URL contract.
 *
 * Filters live in the query string so a search is a link: «вот психологи по
 * тревоге, онлайн, до 5000» has to be something a person can paste into a
 * message, and the back button has to walk the filters the way a reader
 * expects.
 *
 * Every value here is a PRIMITIVE, and lists are comma-joined strings. Two
 * reasons, both about the router: it JSON-parses search values on the way in
 * (so `?specialty=4` would arrive as the number 4) and JSON-stringifies
 * non-strings on the way out (so an array would leave as `?specialty=%5B1%2C4%5D`
 * — unreadable, and not what anyone would type by hand). One string shape for
 * both directions is the only version with no surprises.
 *
 * Parsing is total: anything unreadable becomes the default rather than an
 * error, because this input arrives from a link somebody else wrote.
 *
 * The module stays tiny on purpose — the router imports it eagerly, so anything
 * heavy here lands in the entry chunk.
 */

import type { CatalogFormat, CatalogSort } from './data/repository'

/**
 * Ступени сертификации АЧП в порядке возрастания — значения, которые пишет
 * модерация и принимает фильтр `certLevel` («не ниже»). Живут здесь, в
 * крошечном модуле адреса, потому что их читает разбор ссылки, а роутер
 * грузит этот модуль сразу.
 */
export const CERT_LEVELS = ['basic', 'practitioner', 'master'] as const
export type CertLevel = (typeof CERT_LEVELS)[number]

/** Значение из ссылки или с провода — ступень, если бандл её знает. */
export function asCertLevel(value: unknown): CertLevel | undefined {
  const code = typeof value === 'string' ? value.trim().toLowerCase() : ''
  return (CERT_LEVELS as readonly string[]).includes(code) ? (code as CertLevel) : undefined
}

export interface CatalogSearch {
  q?: string
  /**
   * Specialty ids: a NUMBER for one, a comma-joined string for several (`1,4`).
   * One id as a string left as `?specialty=%223%22` — see `toCatalogSearch`.
   */
  specialty?: string | number
  /** Comma-joined ISO 639-1 codes, e.g. `ru,en`. */
  lang?: string
  format?: CatalogFormat
  priceMax?: number
  /** Валюта, в которой назван `priceMax`. Без неё число в ссылке двусмысленно. */
  priceCurrency?: string
  certified?: boolean
  /**
   * Уровень «не ниже». Уточняет `certified`, а не заменяет его: ссылка с
   * одним `certified=true` открывается как раньше, а сервер, ещё не знающий
   * `certLevel`, просто покажет всех сертифицированных.
   */
  certLevel?: CertLevel
  sort?: CatalogSort
}

const FORMATS: CatalogFormat[] = ['any', 'online', 'offline']
const SORTS: CatalogSort[] = ['rank', 'new']

/** A single id arrives parsed as a number, a list as a string — accept both. */
function csv(value: unknown): string[] {
  const raw = typeof value === 'number' ? String(value) : typeof value === 'string' ? value : ''
  return raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
}

export function parseCatalogSearch(search: Record<string, unknown>): CatalogSearch {
  const format = FORMATS.find((f) => f === search.format)
  const sort = SORTS.find((s) => s === search.sort)
  const priceMax = Number(search.priceMax)
  // Форма кода, а не список валют: список живёт в таблице на сервере, и копия
  // здесь устарела бы на первой же новой валюте. Незнакомый код сервер просто
  // не применит.
  const priceCurrency = typeof search.priceCurrency === 'string'
    && /^[A-Za-z]{3}$/.test(search.priceCurrency)
    ? search.priceCurrency.toUpperCase()
    : undefined
  const specialty = csv(search.specialty).filter((id) => /^\d{1,4}$/.test(id))
  const lang = csv(search.lang)
    .map((code) => code.toLowerCase())
    .filter((code) => /^[a-z]{2}$/.test(code))
  return {
    q: typeof search.q === 'string' && search.q.trim() ? search.q.trim()
      // `?q=2025` роутер читает числом — это тоже запрос, а не мусор.
      : typeof search.q === 'number' && Number.isFinite(search.q) ? String(search.q)
      : undefined,
    // Число и после разбора: адрес, который роутер соберёт из прочитанной
    // ссылки заново, тоже не должен получить кавычек (см. `specialtyParam`).
    specialty: specialtyParam(specialty.map(Number)),
    lang: lang.length ? lang.join(',') : undefined,
    format: format && format !== 'any' ? format : undefined,
    priceMax: Number.isFinite(priceMax) && priceMax > 0 ? Math.floor(priceMax) : undefined,
    priceCurrency,
    certified: search.certified === true || search.certified === 'true' ? true : undefined,
    certLevel: asCertLevel(search.certLevel),
    sort: sort && sort !== 'rank' ? sort : undefined,
  }
}

export function specialtyIds(search: CatalogSearch): number[] {
  return csv(search.specialty).map(Number)
}

/**
 * Одна тема — числом, несколько — строкой через запятую. Роутер
 * JSON-кодирует строку, которая сама разбирается как JSON, чтобы вернуть её
 * строкой: `"3"` уходила в адрес как `?specialty=%223%22`. Число он пишет как
 * есть — `?specialty=3`, а `1,4` JSON-ом не является и остаётся собой. Старые
 * ссылки с кавычками по-прежнему открываются: разбор принимает и строку.
 */
function specialtyParam(ids: number[]): string | number | undefined {
  if (ids.length === 0) return undefined
  return ids.length === 1 ? ids[0] : ids.join(',')
}

export function languageCodes(search: CatalogSearch): string[] {
  return csv(search.lang)
}

/** The inverse — only what differs from the default is written to the URL. */
export function toCatalogSearch(filters: {
  q?: string
  specialties: number[]
  languages: string[]
  format: CatalogFormat
  priceMax?: number
  priceCurrency?: string
  certified: boolean
  certLevel?: CertLevel
  sort: CatalogSort
}): CatalogSearch {
  return {
    q: filters.q?.trim() || undefined,
    specialty: specialtyParam(filters.specialties),
    lang: filters.languages.length ? filters.languages.join(',') : undefined,
    format: filters.format === 'any' ? undefined : filters.format,
    priceMax: filters.priceMax,
    // Валюта живёт в адресе САМА ПО СЕБЕ, без порога. Сначала казалось
    // наоборот — «валюта без числа это мусор в ссылке», — и от этого выбор
    // валюты до ввода суммы молча откатывался: адрес её не сохранял, фильтры
    // читаются из адреса, и селект возвращался к валюте по умолчанию на
    // следующем же кадре. Порядок «сначала валюта, потом сумма» естественный,
    // и он не должен наказываться.
    priceCurrency: filters.priceCurrency,
    // Уровень без галочки не пишется: снятая галочка снимает и уровень.
    certified: filters.certified || Boolean(filters.certLevel) || undefined,
    certLevel: filters.certLevel,
    sort: filters.sort === 'rank' ? undefined : filters.sort,
  }
}
